'use client';

import Link from 'next/link';
import { Box, Typography, Grid } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHighOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import LightbulbIcon from '@mui/icons-material/LightbulbOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircleOutlined';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircleOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckBoxIcon from '@mui/icons-material/CheckBoxOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';

// Icon + color configs for each page
const iconConfig: Record<string, { icon: React.ElementType; colorLight: string; colorDark: string }> = {
    home: { icon: AutoFixHighIcon, colorLight: '#d97706', colorDark: '#f59e0b' },
    docs: { icon: MenuBookIcon, colorLight: '#dc2626', colorDark: '#f87171' },
    examples: { icon: LightbulbIcon, colorLight: '#ca8a04', colorDark: '#facc15' },
    diff: { icon: SyncAltIcon, colorLight: '#0d9488', colorDark: '#2dd4bf' },
    'operations-add': { icon: AddCircleIcon, colorLight: '#16a34a', colorDark: '#4ade80' },
    'operations-remove': { icon: RemoveCircleIcon, colorLight: '#475569', colorDark: '#94a3b8' },
    'operations-replace': { icon: SyncAltIcon, colorLight: '#2563eb', colorDark: '#60a5fa' },
    'operations-move': { icon: DriveFileMoveIcon, colorLight: '#d97706', colorDark: '#fbbf24' },
    'operations-copy': { icon: ContentCopyIcon, colorLight: '#4f46e5', colorDark: '#818cf8' },
    'operations-test': { icon: CheckBoxIcon, colorLight: '#059669', colorDark: '#34d399' },
};

const allPages = {
    home: {
        title: 'JSON Patch Generator',
        description: 'Generate JSON Patch diffs online',
        href: '/',
    },
    docs: {
        title: 'Documentation',
        description: 'Complete RFC 6902 guide',
        href: '/docs',
    },
    examples: {
        title: 'Examples',
        description: 'Real-world JSON Patch scenarios',
        href: '/examples',
    },
    diff: {
        title: 'JSON Diff',
        description: 'Compare two JSON files online',
        href: '/diff',
    },
    'operations-add': {
        title: 'Add Operation',
        description: 'Add values using JSON Patch',
        href: '/operations/add',
    },
    'operations-remove': {
        title: 'Remove Operation',
        description: 'Remove values using JSON Patch',
        href: '/operations/remove',
    },
    'operations-replace': {
        title: 'Replace Operation',
        description: 'Replace values using JSON Patch',
        href: '/operations/replace',
    },
    'operations-move': {
        title: 'Move Operation',
        description: 'Move values using JSON Patch',
        href: '/operations/move',
    },
    'operations-copy': {
        title: 'Copy Operation',
        description: 'Copy values using JSON Patch',
        href: '/operations/copy',
    },
    'operations-test': {
        title: 'Test Operation',
        description: 'Test values using JSON Patch',
        href: '/operations/test',
    },
};

type PageKey = keyof typeof allPages;

interface RelatedPagesProps {
    currentPage: PageKey;
    showOperations?: boolean;
    maxItems?: number;
}

export default function RelatedPages({
    currentPage,
    showOperations = true,
    maxItems = 6
}: RelatedPagesProps) {
    const getRelatedPages = () => {
        const pages: PageKey[] = [];
        if (currentPage !== 'home') pages.push('home');
        if (currentPage !== 'diff') pages.push('diff');
        if (currentPage !== 'docs') pages.push('docs');
        if (currentPage !== 'examples') pages.push('examples');

        if (showOperations) {
            const operations: PageKey[] = [
                'operations-add', 'operations-remove', 'operations-replace',
                'operations-move', 'operations-copy', 'operations-test'
            ];
            operations.forEach(op => {
                if (currentPage !== op) pages.push(op);
            });
        }

        return pages.slice(0, maxItems);
    };

    const relatedPages = getRelatedPages();
    if (relatedPages.length === 0) return null;

    return (
        <Box sx={{ mt: 'var(--space-12)', mb: 'var(--space-8)' }}>
            <Typography
                variant="h2"
                sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 700,
                    mb: 3,
                    pb: 2,
                    color: 'var(--fg)',
                    borderBottom: '1px solid var(--border)',
                }}
            >
                Explore More
            </Typography>

            <Grid container spacing={2}>
                {relatedPages.map((pageKey) => {
                    const page = allPages[pageKey];
                    const config = iconConfig[pageKey] || iconConfig.home;
                    return (
                        <Grid item xs={12} sm={6} md={4} key={pageKey}>
                            <Link href={page.href} style={{ textDecoration: 'none' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1.5,
                                        px: 1,
                                        borderRadius: 'var(--radius-lg)',
                                        border: '1px solid transparent',
                                        transition: 'all var(--duration-fast) var(--ease-default)',
                                        '&:hover': {
                                            backgroundColor: 'var(--accent-subtle)',
                                            borderColor: 'var(--accent-muted)',
                                            transform: 'translateY(-1px)',
                                            '& .link-title': {
                                                color: 'var(--accent) !important',
                                            },
                                        },
                                    }}
                                >
                                    <config.icon
                                        sx={{
                                            fontSize: '20px',
                                            color: 'var(--accent)',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            className="link-title"
                                            sx={{
                                                fontSize: 'var(--font-size-base)',
                                                fontWeight: 600,
                                                color: 'var(--fg)',
                                                transition: 'color var(--duration-fast) var(--ease-default)',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {page.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--fg-muted)',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {page.description}
                                        </Typography>
                                    </Box>
                                    <ArrowForwardIcon
                                        sx={{ fontSize: '16px', color: 'var(--fg-subtle)', marginLeft: 'auto', flexShrink: 0 }}
                                    />
                                </Box>
                            </Link>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}
